// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import { AddressSchema } from './backend_config.types';
import { LosslessNumber } from './lossless-json-codec';
import type { BaseIpwTransactionDetails, TransactionDetails } from './payment.types';
import {
    JsonLosslessNumberInputSchema,
    JsonPositiveLosslessNumberInputSchema,
    PhoneNumberSchema,
} from './schemas/common.schemas';
import { TransactionDetailsSchema } from './schemas/payment.schemas';
import { ThirdPartySystemIdSchema } from './schemas/third-party-system.schemas';
import { TransactionDetailsTypeId } from './types/transaction-details.enums';
import { ThirdPartySystemId } from './types/third-party-system.enums';

export const StateType = {
    INIT: 'init',
    LIST_DISPLAY: 'list-display',
    OBJECT_DISPLAY: 'object-display',
    PAYMENTS_HOME: 'payments-home',
    CARD_PAYMENT: 'card-payment',
    ACH_PAYMENT: 'ach-payment',
    TERMINAL_PAYMENT: 'terminal-payment',
    E_INVOICE: 'e-invoice',
    TRANSACTION_STATUS: 'transaction-status',
    RECENT_PAYMENTS: 'recent-payments',
    RECENT_PAYMENT_REQUESTS: 'recent-payment-requests',
    PAYMENT_REQUEST_DETAILS: 'payment-request-details',
    PAYMENTS_AUDIT_LOG: 'payments-audit-log',
    TRANSACTION_DETAILS: 'transaction-details',
    RECONCILE_PAYMENTS: 'reconcile-payments',
} as const;

export type StateType = (typeof StateType)[keyof typeof StateType];

export type CardPaymentInitialTransactionDetails = TransactionDetails | BaseIpwTransactionDetails;

export interface EInvoiceInitialScreenData {
    transactionDetails: CardPaymentInitialTransactionDetails;
    disableDetection?: boolean;
    merchantUserUuid?: string;
    recipientEmail?: string;
    recipientPhone?: string;
    sendEmail?: boolean;
    sendSms?: boolean;
}

export interface EInvoiceAppStateFields {
    type: typeof StateType.E_INVOICE;
    data: null | EInvoiceInitialScreenData;
}

export const ReconciliationTargetKind = {
    PAYMENT_REQUEST: 'payment-request',
    PAYMENT_TRANSACTION: 'payment-transaction',
} as const;

export type ReconciliationTargetKind =
    (typeof ReconciliationTargetKind)[keyof typeof ReconciliationTargetKind];

export interface PaymentRequestReconciliationTarget {
    kind: typeof ReconciliationTargetKind.PAYMENT_REQUEST;
    payment_request_uuid: string;
}

export interface PaymentTransactionReconciliationTarget {
    kind: typeof ReconciliationTargetKind.PAYMENT_TRANSACTION;
    payment_transaction_uuid: string;
}

// Discriminator: kind
export type ReconciliationTarget =
    | PaymentRequestReconciliationTarget
    | PaymentTransactionReconciliationTarget;

export interface ReconcilePaymentsData {
    target?: ReconciliationTarget;
}

export interface SidebarConfig {
    enable_manual_transaction_details?: boolean;
}

export function createGenericTransactionDetailsDraft(): CardPaymentInitialTransactionDetails {
    return {
        amount: new LosslessNumber('0'),
        currency: 'USD',
        ipw_system_id: ThirdPartySystemId.GENERIC,
        transaction_details_type_id: TransactionDetailsTypeId.GENERIC,
        transaction_details_type_version: 1,
    };
}

export const TransactionDetailsTypeIdSchema: z.ZodType<TransactionDetailsTypeId> = z.union([
    z.literal(TransactionDetailsTypeId.GENERIC),
    z.literal(TransactionDetailsTypeId.DEALERTRACK_PARTS),
    z.literal(TransactionDetailsTypeId.DEALERTRACK_SERVICE),
    z.literal(TransactionDetailsTypeId.DEALERTRACK_DEAL),
    z.literal(TransactionDetailsTypeId.DEALERTRACK_LEASING),
    z.literal(TransactionDetailsTypeId.DEALERTRACK_AR_STATEMENT_CUSTOMERS),
]);

export const CardPaymentInitialTransactionDetailsSchema: z.ZodType<CardPaymentInitialTransactionDetails> =
    z.union([
        TransactionDetailsSchema,
        z
            .object({
                amount: JsonLosslessNumberInputSchema,
                detectedAmount: JsonPositiveLosslessNumberInputSchema.optional(),
                tax: JsonLosslessNumberInputSchema.optional(),
                fee: JsonLosslessNumberInputSchema.optional(),
                surcharge: JsonLosslessNumberInputSchema.optional(),
                currency: z.string().min(1),
                merchant_transaction_id: z.string().optional(),
                customerName: z.string().optional(),
                mainPhone: PhoneNumberSchema.optional(),
                otherPhones: z.array(PhoneNumberSchema).default([]),
                mainEmail: z.string().email().optional(),
                address: AddressSchema.optional(),
                description: z.string().optional(),
                notes: z.string().optional(),
                ipw_customer_id: z.string().optional(),
                ipw_system_id: ThirdPartySystemIdSchema,
                transaction_details_type_id: TransactionDetailsTypeIdSchema,
                transaction_details_type_version: z.number(),
            })
            .passthrough(),
    ]);

export const EInvoiceInitialScreenDataSchema: z.ZodType<EInvoiceInitialScreenData> = z.object({
    transactionDetails: CardPaymentInitialTransactionDetailsSchema,
    disableDetection: z.boolean().optional(),
    merchantUserUuid: z.string().uuid().optional(),
    recipientEmail: z.string().email().optional(),
    recipientPhone: z.string().optional(),
    sendEmail: z.boolean().optional(),
    sendSms: z.boolean().optional(),
});

const PaymentRequestReconciliationTargetSchemaShape: {
    kind: z.ZodLiteral<typeof ReconciliationTargetKind.PAYMENT_REQUEST>;
    payment_request_uuid: z.ZodString;
} = {
    kind: z.literal(ReconciliationTargetKind.PAYMENT_REQUEST),
    payment_request_uuid: z.string().uuid(),
};

export const PaymentRequestReconciliationTargetSchema: z.ZodObject<
    typeof PaymentRequestReconciliationTargetSchemaShape
> = z.object(PaymentRequestReconciliationTargetSchemaShape).strict();

const PaymentTransactionReconciliationTargetSchemaShape: {
    kind: z.ZodLiteral<typeof ReconciliationTargetKind.PAYMENT_TRANSACTION>;
    payment_transaction_uuid: z.ZodString;
} = {
    kind: z.literal(ReconciliationTargetKind.PAYMENT_TRANSACTION),
    payment_transaction_uuid: z.string().uuid(),
};

export const PaymentTransactionReconciliationTargetSchema: z.ZodObject<
    typeof PaymentTransactionReconciliationTargetSchemaShape
> = z.object(PaymentTransactionReconciliationTargetSchemaShape).strict();

export const ReconciliationTargetSchema: z.ZodType<ReconciliationTarget> = z.discriminatedUnion(
    'kind',
    [PaymentRequestReconciliationTargetSchema, PaymentTransactionReconciliationTargetSchema]
);

export const ReconcilePaymentsDataSchema: z.ZodType<ReconcilePaymentsData> = z
    .object({
        target: ReconciliationTargetSchema.optional(),
    })
    .strict();
